import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { PortalNavbarComponent } from './portal-navbar.component';
import { AuthService } from '../services/auth.service';
import { LegalConsentService } from '../services/legal-consent.service';

@Component({
  selector: 'app-terms-and-conditions-page',
  standalone: true,
  imports: [CommonModule, RouterModule, PortalNavbarComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>
      <section class="content">
        <div class="content-inner">
          <h1>Terms &amp; Conditions – United Team Building</h1>

          <h2>1. Acceptance of Terms</h2>
          <p>By accessing or using United Team Building, you agree to be bound by these Terms &amp; Conditions and all applicable laws.</p>

          <h2>2. SMS Terms</h2>
          <p>By opting in to SMS notifications:</p>
          <ul>
            <li>You consent to receive transactional text messages.</li>
            <li>Message frequency varies.</li>
            <li>Message and data rates may apply.</li>
            <li>Reply STOP to opt out.</li>
            <li>Reply HELP for help.</li>
          </ul>
          <p>Consent is not a condition of purchase.</p>

          <h2>3. User Content &amp; Media Consent</h2>
          <h3>Ownership</h3>
          <p>You retain ownership of all media and content you upload.</p>
          <h3>License Grant</h3>
          <p>By uploading content, you grant United Team Building a perpetual, worldwide, royalty-free, nonexclusive license to:</p>
          <ul>
            <li>Host</li>
            <li>Use</li>
            <li>Reproduce</li>
            <li>Display</li>
            <li>Distribute</li>
          </ul>
          <p>This license is granted solely to operate, improve, and promote the platform and related services.</p>
          <h3>User Representations</h3>
          <p>You represent and warrant that:</p>
          <ul>
            <li>You own or have the necessary rights to the content.</li>
            <li>The content does not violate any third-party rights.</li>
            <li>The content is lawful and appropriate.</li>
          </ul>

          <h2>4. Prohibited Content</h2>
          <p>You may not upload content that is:</p>
          <ul>
            <li>Illegal</li>
            <li>Defamatory</li>
            <li>Pornographic</li>
            <li>Harassing</li>
            <li>Infringing on intellectual-property or privacy rights</li>
          </ul>

          <h2>5. Moderation &amp; Removal</h2>
          <p>United Team Building:</p>
          <ul>
            <li>Reserves the right to moderate or remove content at any time.</li>
            <li>Is not responsible for user-generated content.</li>
            <li>Disclaims liability for damages arising from user-submitted content.</li>
          </ul>

          <h2>6. Limitation of Liability</h2>
          <p>To the fullest extent permitted by law, United Team Building shall not be liable for any indirect, incidental, or consequential damages.</p>

          <h2>7. Changes to Terms</h2>
          <p>We may update these Terms at any time. Continued use of the service constitutes acceptance of the updated terms.</p>

          <h2>8. Contact</h2>
          <p>For questions: <a href="mailto:support@unitedteambuilding.com">support@unitedteambuilding.com</a></p>

          <p class="muted">Read this page and accept the terms to continue using protected platform features.</p>

          @if (message()) {
            <div class="status">{{ message() }}</div>
          }

          @if (isLoggedIn()) {
            <button class="btn" [disabled]="busy()" (click)="acceptTerms()">I Accept Terms and Conditions</button>
          }
        </div>
      </section>
    </div>
  `,
  styles: [`
    .content { padding: 2rem 1.25rem 4rem; }
    .content-inner { max-width: 920px; margin: 0 auto; border: 1px solid var(--color-border); border-radius: .9rem; padding: 1.1rem 1.2rem; background: color-mix(in srgb, var(--color-panel) 80%, transparent); }
    h1 { margin-bottom: .9rem; }
    h2 { margin-top: 1.1rem; margin-bottom: .35rem; font-size: 1.03rem; }
    h3 { margin-top: .7rem; margin-bottom: .3rem; font-size: .95rem; }
    p, li { line-height: 1.45; }
    ul { margin-top: .25rem; margin-bottom: .55rem; }
    a { color: var(--color-accent-soft); }
    .muted { color: var(--color-muted); }
    .status { margin: .8rem 0; padding: .65rem .75rem; border-radius: .6rem; border: 1px solid color-mix(in srgb, var(--color-accent-soft) 45%, transparent); background: color-mix(in srgb, var(--color-accent-soft) 12%, transparent); }
    .btn { margin-top: .75rem; border: 1px solid var(--color-accent); background: var(--color-accent); color: #fff; border-radius: .55rem; padding: .55rem .95rem; font-weight: 600; cursor: pointer; }
    .btn:disabled { opacity: .55; cursor: not-allowed; }
  `]
})
export class TermsAndConditionsPageComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly legalConsentService = inject(LegalConsentService);
  private readonly router = inject(Router);

  readonly busy = signal(false);
  readonly message = signal('');
  readonly isLoggedIn = signal(false);

  ngOnInit(): void {
    this.isLoggedIn.set(this.authService.getActiveAccount() !== null);
  }

  async acceptTerms(): Promise<void> {
    if (!this.isLoggedIn() || this.busy()) {
      return;
    }

    this.busy.set(true);
    this.message.set('');

    try {
      await firstValueFrom(this.legalConsentService.acceptTerms());
      this.authService.invalidateLegalConsentCache();

      const pendingRoute = await this.authService.getPendingLegalRouteAsync(true);
      if (pendingRoute) {
        await this.router.navigate([pendingRoute], { replaceUrl: true });
        return;
      }

      const roles = await this.authService.getRolesAsync();
      const targetRoute = roles.includes('TeamMember') ? '/my-media' : '/home';
      await this.router.navigate([targetRoute], { replaceUrl: true });
    } catch {
      this.message.set('Unable to accept terms right now. Please try again.');
    } finally {
      this.busy.set(false);
    }
  }
}
